import { DREAMER_FLOAT, PULSE_REST, SHEETS } from '../../models/lidless-dreamer.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Soñador sin párpados (models/lidless-dreamer.ts): it does not move like a living thing. It hangs upright in the
 * air and drifts on a slow swell, the sheets under it stirring as if in water; the crown of eyes turns round its
 * head without stopping (one eye's step each loop, so every loop meets the next). When it moves it glides, the
 * sheets streaming back.
 *
 * Conventions: +X on the body or head bows it forward; on an arm −X swings it forward and up, +Z lifts the right
 * arm (+X) out to the side and −Z the left; −X on a forearm bends the elbow. +X on a sheet swings its hem back.
 * `crown` turns on Y. `lids` is scaled to nothing to open the eyes (the awake look has none: the same clips leave
 * it nothing to hide); `eyes` swells with the pulse. `pulse` is the ring of violet, built tiny: scaled by `RING`
 * it spreads over the sector.
 *
 * Moments: attack (it lifts a hand and the crown flares at a hero) strikes at 0.5 s. Open: the lids go at
 * 0.4–0.5 s, the ring goes out from 0.5 s to 1.2 s (the sector falls asleep at 0.5 s), the lids close again by
 * 1.6 s. Blink: it thins to a thread and is gone at 0.35 s, nothing until 0.6 s, back whole by 0.95 s.
 */

type S = `sheet${(typeof SHEETS)[number]}${1 | 2}`;
type B = 'root' | 'body' | 'head' | 'crown' | 'eyes' | 'lids' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'pulse' | S;
type P = KeyPose<B>;

/** How far the ring of the pulse spreads (×2.5 its built size: a few hexes round it). */
const RING = 2.5 / PULSE_REST;
/** One eye's step round the crown (nine eyes). */
const STEP = TAU / 9;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** The sheets stir on their own phases, the lower piece more and later; `trail` streams them back. */
const sheets = (b: Bones<B>, t: number, amp: number, trail: number, freq = 0.35): void => {
  SHEETS.forEach((n, i) => {
    const o = i * 1.25;
    const w = TAU * freq * t;
    b[`sheet${n}1`].rot(trail * 0.5 + amp * Math.sin(w + o), 0, amp * 0.6 * Math.cos(w * 0.9 + o));
    b[`sheet${n}2`].rot(trail * 0.6 + amp * 1.6 * Math.sin(w + o - 0.9), 0, amp * 1.2 * Math.cos(w * 0.9 + o - 0.9));
  });
};

const float = (dur: number, name: string, trail: number, lean: number): Clip<B> =>
  clip<B>(name, dur, true, (b, t) => {
    const p = phase(t, dur) * TAU;
    b.root.pos(0, 0.07 * Math.sin(p), 0).rot(lean * 0.4, 0, 0.02 * Math.sin(p + 0.8));
    b.body.rot(lean + 0.02 * Math.sin(p + 0.5), 0.04 * Math.sin(p * 0.5 * 2), 0.015 * Math.cos(p));
    b.head.rot(0.22 - lean * 0.5 + 0.04 * Math.sin(p + 1.4), 0.08 * Math.sin(p + 0.3), 0.04 * Math.sin(p + 2));
    b.crown.rot(0, STEP * phase(t, dur), 0);
    b.armL.rot(0.04 * Math.sin(p + 0.6) + trail * 0.25, 0, -0.03 * Math.sin(p));
    b.armR.rot(0.04 * Math.sin(p + 1.6) + trail * 0.25, 0, 0.03 * Math.sin(p));
    b.foreL.rot(-0.06 * Math.sin(p + 1.1));
    b.foreR.rot(-0.06 * Math.sin(p + 2.1));
    sheets(b, t, 0.1 + trail * 0.05, trail, 2 / dur);
  }, (t) => {
    const s = Math.sin(phase(t, dur) * TAU);
    return [1 - 0.008 * s, 1 + 0.012 * s, 1 - 0.008 * s];
  });

/** It lifts its right hand toward a hero, fingers spread; the crown flares and the eyes swell (0.5 s). */
const attack = (): Clip<B> =>
  clip<B>('attack', 1.3, false, (b, t) => {
    const reach: P = { body: [-0.08, 0.15, 0], head: [-0.15, 0.1, 0], armR: [-1.3, 0, 0.15], foreR: [-0.15, 0, 0], armL: [0.1, 0, -0.12] };
    timeline(b, t, [
      [0, {}],
      [0.38, reach, inOut],
      [0.5, { ...reach, root: [0, 0, 0, 0, 0.04, 0.04], armR: [-1.45, 0, 0.12], head: [-0.25, 0.1, 0] }, outQuad],
      [0.75, reach, inOut],
      [1.3, {}, inOut],
    ], { foreR: 0.03 });
    const flare = envelope(t, 0.42, 0.5, 0.62, 0.85);
    b.eyes.scale(1 + 0.25 * flare);
    b.lids.scale(1 - 0.99 * envelope(t, 0.4, 0.46, 0.66, 0.78));
    b.crown.rot(0, 0.3 * envelope(t, 0.3, 0.6, 0.9, 1.3), 0);
    sheets(b, t, 0.12, 0.25 * flare, 0.8);
  });

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.root.pos(0, 0.02 * q, -0.06 * q);
    b.body.rot(-0.14 * q, 0.06 * q, 0.04 * q);
    b.head.rot(-0.3 * q, 0, 0.12 * q);
    b.crown.rot(0.15 * q, 0.2 * q, 0);
    b.armL.rot(0.2 * q, 0, -0.15 * q);
    b.armR.rot(0.2 * q, 0, 0.15 * q);
    sheets(b, t, 0.05, -0.4 * q);
  });

/** Stunned: it tilts and turns slowly in the air, the crown wobbling off its head, the sheets limp. */
const stun = (): Clip<B> =>
  clip<B>('stun', 2.6, true, (b, t) => {
    const p = phase(t, 2.6) * TAU;
    b.root.pos(0, -0.12 + 0.03 * Math.sin(p), 0).rot(0.06 * Math.sin(p), 0, 0.12 * Math.cos(p));
    b.body.rot(0.18, 0, 0);
    b.head.rot(0.5 + 0.1 * Math.sin(p + 0.8), 0.2 * Math.cos(p + 0.8), 0.15 * Math.sin(p + 0.8));
    b.crown.rot(0.2 * Math.sin(p), STEP * phase(t, 2.6), 0.2 * Math.cos(p));
    b.armL.rot(0.15, 0, 0.1);
    b.armR.rot(0.15, 0, -0.1);
    sheets(b, t, 0.04, 0, 0.4);
  });

/**
 * The death (2.6 s): every eye opens at once (0.1 s), the head thrown back; then whatever held it up lets go:
 * it drops (1.0 s on the ground), the sheets fold over it and it sinks into them, the crown rolling off.
 */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.2, { root: [0, 0, 0, 0, 0.1, 0], body: [-0.3, 0, 0], head: [-0.7, 0, 0], armL: [-0.6, 0, -0.8], armR: [-0.6, 0, 0.8], foreL: [-0.3, 0, 0], foreR: [-0.3, 0, 0] }, outExpo],
      [0.55, { root: [0, 0, 0, 0, 0.04, 0], body: [0.2, 0, 0.1], head: [0.6, 0.2, 0.3], armL: [0.2, 0, 0.1], armR: [0.25, 0, -0.1] }, inOut],
      [1.0, { root: [0.1, 0, 0.15, 0, -DREAMER_FLOAT - 0.25, 0], body: [0.5, 0.2, 0.2], head: [0.8, 0.3, 0.4], armL: [0.4, 0, 0.3], armR: [0.5, 0, -0.4], foreL: [0.3, 0, 0], foreR: [0.3, 0, 0], crown: [0.8, 0, 0.6, 0.1, -0.2, 0.2] }, inQuad],
      [2.6, { root: [0.14, 0, 0.2, 0, -DREAMER_FLOAT - 0.9, 0], body: [1.2, 0.2, 0.3], head: [0.9, 0.4, 0.5], armL: [0.6, 0, 0.6], armR: [0.7, 0, -0.7], foreL: [0.4, 0, 0], foreR: [0.4, 0, 0], crown: [1.4, 0, 1.3, 0.3, -0.75, 0.35] }, outCubic],
    ], { head: 0.05 });
    b.lids.scale(1 - 0.99 * envelope(t, 0.05, 0.12, 0.8, 1.2));
    b.eyes.scale(1 + 0.3 * envelope(t, 0.05, 0.15, 0.4, 0.9) - 0.6 * span(t, 1.2, 2.4));
    const limp = span(t, 0.9, 1.6);
    sheets(b, t, 0.2 * (1 - limp), -0.5 * envelope(t, 0.1, 0.25, 0.5, 0.9) + 1.1 * limp, 1.5);
  }, (t) => {
    const s = span(t, 1.0, 2.4, outCubic);
    return [1 + 0.3 * s, 1 - 0.55 * s, 1 + 0.3 * s];
  });

/**
 * Open (1.8 s): it lifts its bowed head; every lid on the crown opens at once (0.4–0.5 s), the eyes swell, and a
 * ring of violet goes out over the sector (0.5–1.2 s) and fades; the head bows again and the lids close (to 1.6 s).
 */
const open = (): Clip<B> =>
  clip<B>('open', 1.8, false, (b, t) => {
    const lift: P = { root: [0, 0, 0, 0, 0.12, 0], body: [-0.12, 0, 0], head: [-0.4, 0, 0], armL: [-0.2, 0, -0.45], armR: [-0.2, 0, 0.45], foreL: [-0.3, 0, 0], foreR: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.4, lift, inOut],
      [0.5, { ...lift, root: [0, 0, 0, 0, 0.16, 0], armL: [-0.3, 0, -0.65], armR: [-0.3, 0, 0.65] }, outExpo],
      [1.2, lift, inOut],
      [1.8, {}, inOut],
    ], { head: 0.04 });
    b.lids.scale(1 - 0.99 * envelope(t, 0.38, 0.48, 1.25, 1.6, inOut));
    b.eyes.scale(1 + 0.3 * envelope(t, 0.45, 0.52, 0.7, 1.3));
    b.crown.rot(0, STEP * span(t, 0.4, 1.6, inOut), 0).scale(1 + 0.12 * envelope(t, 0.45, 0.52, 0.8, 1.3));
    const ring = span(t, 0.5, 1.2, outCubic);
    b.pulse.scale(t < 0.5 || t > 1.25 ? 1 : 1 + (RING - 1) * ring);
    sheets(b, t, 0.12, -0.6 * envelope(t, 0.45, 0.52, 0.8, 1.4), 0.7);
  }, (t) => {
    const k = Math.max(0, kick(t, 0.5, 3, 7));
    return [1 - 0.03 * k, 1 + 0.05 * k, 1 - 0.03 * k];
  }, 'Abrir los ojos');

/**
 * Blink (1.2 s): the eyes snap open, it thins to a thread of itself and is gone (0.35 s); nothing until 0.6 s, then
 * the thread draws out again where it is now and fills back into the dreamer (0.95 s), the sheets settling.
 */
const blink = (): Clip<B> =>
  clip<B>('blink', 1.2, false, (b, t) => {
    const out = span(t, 0.12, 0.35, inCubic);
    const back = span(t, 0.6, 0.95, outBack);
    const gone = t >= 0.35 && t < 0.6;
    const k = gone ? 0.01 : t < 0.35 ? 1 - 0.99 * out : 0.01 + 0.99 * back;
    b.root.scale(k).pos(0, 0.25 * (1 - k), 0);
    b.lids.scale(1 - 0.99 * envelope(t, 0.02, 0.1, 0.8, 1.1));
    b.head.rot(-0.3 * envelope(t, 0, 0.1, 0.9, 1.2), 0, 0);
    b.crown.rot(0, 1.2 * (1 - k), 0);
    sheets(b, t, 0.1, 0.8 * (1 - k), 1.2);
  }, (t) => {
    // It stretches into a thread as it goes and comes back the same way.
    const thin = t < 0.35 ? span(t, 0.05, 0.33, inQuad) : t < 0.6 ? 1 : 1 - span(t, 0.62, 0.95, outQuad);
    return [1 - 0.85 * thin, 1 + 0.9 * thin, 1 - 0.85 * thin];
  }, 'Desvanecerse');

/** Clips by library kind (see ../rigs.ts): the same for both looks. */
export const lidlessDreamerClips = (): Clip[] => [float(4.0, 'idle', 0, 0), float(2.8, 'walk', 0.45, 0.12), attack(), hit(), stun(), die(), open(), blink()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { lidlessDreamer: lidlessDreamerClips, lidlessDreamerAwake: lidlessDreamerClips };
